import { describeMutationError } from "@api/mutationErrors";
import { useWriteErrorStore } from "@api/writeErrorStore";
import { ClinicAuthError } from "@auth/clinicAuthClient";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseInfiniteQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  fetchCallTodos,
  fetchOpenTasks,
  fetchTaskCounts,
  submitTaskAction,
  type Task,
} from "@tasks/tasksClient";

export const tasksKeys = {
  all: ["tasks"] as const,
  counts: (sessionKey: string) =>
    ["tasks", sessionKey, "counts", "me"] as const,
  open: (sessionKey: string) => ["tasks", sessionKey, "open"] as const,
  call: (sessionKey: string, callUuid: string) =>
    ["tasks", sessionKey, "call", callUuid] as const,
};

export const useTaskCounts = (sessionKey: string) =>
  useSuspenseQuery({
    queryKey: tasksKeys.counts(sessionKey),
    queryFn: () => fetchTaskCounts("me"),
  });

/** Ordered server-side; paging follows the opaque `nextCursor`, never a short page. */
export const useOpenTasks = (sessionKey: string) =>
  useSuspenseInfiniteQuery({
    queryKey: tasksKeys.open(sessionKey),
    queryFn: ({ pageParam }) => fetchOpenTasks(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

/** Under `tasksKeys.all`, so task frames and task writes refresh it. */
export const useCallTodos = (sessionKey: string, callUuid: string) =>
  useQuery({
    queryKey: tasksKeys.call(sessionKey, callUuid),
    queryFn: () => fetchCallTodos(callUuid),
  });

/** A 409 means someone closed it first; the refetch shows it. */
export const useCompleteTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (task: Task) => submitTaskAction(task.id, "complete", {}),
    meta: { handled: true },
    onError: (error) => {
      if (error instanceof ClinicAuthError && error.status === 409) return;
      useWriteErrorStore
        .getState()
        .show(describeMutationError(error, "close that to-do"));
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: tasksKeys.all }),
  });
};
